import { useEffect, useState } from 'react';
import { useReducedMotion } from '@/app/providers/motion-provider';
import type { SlothPlayback } from './sloth-assets';

/** 下一格：`random` 隨機挑一格不同的，`loop` 依序前進。 */
export function nextFrameIndex(
  current: number,
  frameCount: number,
  playback: SlothPlayback,
  random: () => number = Math.random,
): number {
  if (frameCount < 2) return 0;
  if (playback.mode !== 'random') return (current + 1) % frameCount;

  // 從「除了自己以外」的格數裡挑，換格保證看得出來。
  // random() 理論上不會回傳 1，但真的回傳時 offset 會繞回原地，所以夾住上限。
  const offset = Math.min(Math.floor(random() * (frameCount - 1)), frameCount - 2);
  return (current + 1 + offset) % frameCount;
}

/** 這一格要停留多久。 */
export function frameDelay(playback: SlothPlayback, random: () => number = Math.random): number {
  if (playback.mode === 'loop') return playback.frameMs;
  if (playback.mode === 'static') return Number.POSITIVE_INFINITY;
  const span = Math.max(0, playback.maxMs - playback.minMs);
  return playback.minMs + random() * span;
}

/**
 * 樹懶待機動畫的計時器（spec 23.2「持續動畫只允許在隔離且 memoized 的樹懶待機」）。
 *
 * 只回傳目前該顯示第幾格，不碰 DOM；換格由呼叫端用 opacity 處理，
 * 全程只動 opacity，不會讓主工作區重繪。
 *
 * 以下情況直接停在第 0 格，而且不排任何計時器：
 * 動畫等級解析成「減少動態」、只有一格、或播放模式是 static。
 * 等級的預設是「完整動畫」，所以作業系統的 prefers-reduced-motion
 * 不會自己讓樹懶停下來——要停得由使用者在設定頁明說（spec 23.3）。
 */
export function useSlothFrame(frameCount: number, playback: SlothPlayback): number {
  const [index, setIndex] = useState(0);
  const reducedMotion = useReducedMotion();
  const animated = !reducedMotion && frameCount > 1 && playback.mode !== 'static';

  useEffect(() => {
    if (!animated) return;

    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        setIndex((current) => nextFrameIndex(current, frameCount, playback));
        schedule();
      }, frameDelay(playback));
    };
    schedule();

    return () => clearTimeout(timer);
  }, [animated, frameCount, playback]);

  // 停用動畫時直接在 render 算出第 0 格，不用 effect 再回寫一次 state。
  if (!animated) return 0;
  // 素材換掉之後格數可能變少，夾住避免指到不存在的格。
  return frameCount > 0 ? Math.min(index, frameCount - 1) : 0;
}
